import { useMemo } from "react";

import { Link } from "@/components/ui/Link";
import { Column, createDataColumn } from "@/components/ui/table-final-saviour/Table";
import { ExpandedContent } from "@/components/ui/table-final-saviour/Table/TableExpandable";
import { routes } from "@/routes";
import { generatePath } from "@/routes/routes.utils";
import { useAppSelector, primaryActiveRoleSelector } from "@/store";
import { fixedNumberPrecision } from "@/utils/number.utils";
import { notNil } from "@/utils/validation.utils";

type DataRow = {
    "Parts Cost"?: number | null;
    "Parts Revenue"?: number | null;
    "Parts Event": string;
    "Parts Name": string;
    "Parts No.": string | null;
    "Parts Serial No.": string | null;
    "Parts Total": number;
};

type Props = {
    data: DataRow[];
    hideFinancialColumns?: boolean;
};

export const PartsSection = (props: Props) => {
    const { data, hideFinancialColumns } = props;

    const primaryActiveRole = useAppSelector(primaryActiveRoleSelector);
    const isTeamLead = primaryActiveRole?.name === "Team Lead";
    const isServiceManager = primaryActiveRole?.name === "Service Manager";

    const showFinancialColumns = isServiceManager && hideFinancialColumns !== true;

    const columns: Column<DataRow>[] = useMemo(
        () => [
            createDataColumn({
                title: "Name",
                dataKey: "Parts Name",
                dataType: "string",
            }),
            createDataColumn({
                title: "No.",
                dataKey: "Parts No.",
                dataType: "string",
                getCellValue: ({ row }) => {
                    const partsNo = row["Parts No."];

                    if (isTeamLead && partsNo) {
                        const redirectPath =
                            generatePath(routes.partDetail, {
                                params: {
                                    partNumber: partsNo,
                                },
                            }) ?? null;

                        if (redirectPath) return <Link to={redirectPath}>{partsNo.toString()}</Link>;
                    }

                    return partsNo;
                },
            }),
            createDataColumn({
                title: "Serial No.",
                dataKey: "Parts Serial No.",
                dataType: "string",
            }),
            createDataColumn({
                title: "Total",
                dataKey: "Parts Total",
                dataType: "number",
                align: "right",
                width: 140,
            }),
            ...(showFinancialColumns
                ? [
                      createDataColumn<DataRow>({
                          title: "Revenue",
                          dataKey: "Parts Revenue",
                          dataType: "number",
                          align: "right",
                          width: 140,
                          getCellValue: ({ row: { "Parts Revenue": value } }) =>
                              notNil(value) ? fixedNumberPrecision(value) : value,
                      }),
                      createDataColumn<DataRow>({
                          title: "Cost",
                          dataKey: "Parts Cost",
                          dataType: "number",
                          align: "right",
                          width: 140,
                          getCellValue: ({ row: { "Parts Cost": value } }) =>
                              notNil(value) ? fixedNumberPrecision(value) : value,
                      }),
                  ]
                : []),
            createDataColumn({
                title: "Event",
                dataKey: "Parts Event",
                dataType: "number",
                width: 200,
            }),
        ],
        [isTeamLead, showFinancialColumns],
    );

    if (data.length === 0) {
        return null;
    }

    return (
        <ExpandedContent.Section>
            <ExpandedContent.Section.Content>
                <ExpandedContent.Section.Content.Title>Parts</ExpandedContent.Section.Content.Title>
            </ExpandedContent.Section.Content>

            <ExpandedContent.Section.Table<DataRow>
                dataSource={{ isLoading: false, isFetching: false, data }}
                columns={columns}
                maxHeight={400}
            />
        </ExpandedContent.Section>
    );
};
